import React, { useCallback, useEffect, useState } from "react";
import { CreditCard, Landmark, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { goToCheckout } from "@/lib/checkout";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const PENDING_KEY = "findloop_pm_pending";

// Mandatory payment method (card OR IBAN) before any paid offer; resumes the offer after Stripe.
export function usePaymentMethodGate(onResume) {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null);

  const refresh = useCallback(() => api("/auth/me/payment-method").then((r) => { setInfo(r); return r; }).catch(() => ({ has_method: false })), []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("pm_setup");
    // Link from the "card expires soon" email → open the replacement dialog.
    if (params.get("pm") === "change") { window.history.replaceState(null, "", window.location.pathname); setPending(null); setOpen(true); }
    if (!sid) { refresh(); return; }
    window.history.replaceState(null, "", window.location.pathname);
    api("/auth/me/payment-method/confirm", { method: "POST", body: { session_id: sid } })
      .then((r) => {
        setInfo(r);
        let p = null;
        try { p = JSON.parse(sessionStorage.getItem(PENDING_KEY) || "null"); sessionStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
        if (p) onResume?.(p);
      })
      .catch((e) => { toast.error(e.message); refresh(); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const require = useCallback(async (action, run) => {
    const cur = info || await refresh();
    if (cur?.has_method) return run();
    setPending(action);
    setOpen(true);
  }, [info, refresh]);

  const change = () => { setPending(null); setOpen(true); };
  return { info, require, change, dialogProps: { open, onClose: () => setOpen(false), pending } };
}

export const PaymentMethodDialog = ({ open, onClose, pending }) => {
  const { t } = useLang();
  const [method, setMethod] = useState(null);
  const [busy, setBusy] = useState(false);

  async function go() {
    if (!method) return;
    setBusy(true);
    try {
      if (pending) sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
      const r = await api("/auth/me/payment-method/setup", { method: "POST", body: { method, origin_url: window.location.origin, return_path: window.location.pathname } });
      goToCheckout(r.url);
    } catch (e) { toast.error(e.message); setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="pm-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#191A54]">{t("pm.title")}</DialogTitle>
          <DialogDescription>{t("pm.required")}</DialogDescription>
        </DialogHeader>
        <MethodChoice value={method} onChange={setMethod} />
        <Button onClick={go} disabled={!method || busy} className="w-full h-12 rounded-full bg-[#191A54] hover:bg-[#191A54]/90 font-semibold" data-testid="pm-continue">
          <ShieldCheck size={16} className="mr-1.5" /> {t("pm.continue")}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

// Exclusive choice: bank card OR SEPA IBAN.
export const MethodChoice = ({ value, onChange, testid = "pm-option" }) => {
  const { t } = useLang();
  const opt = (key, Icon, label) => (
    <button type="button" onClick={() => onChange(key)} aria-pressed={value === key} data-testid={`${testid}-${key}`}
      className={`w-full flex items-center gap-3 rounded-2xl border-2 p-4 text-left transition-[border-color,background-color] ${value === key ? "border-[#191A54] bg-[#191A54]/5" : "border-border hover:border-[#191A54]/40"}`}>
      <span className={`h-5 w-5 rounded-full border-2 grid place-items-center ${value === key ? "border-[#191A54]" : "border-slate-300"}`}>
        {value === key && <span className="h-2.5 w-2.5 rounded-full bg-[#191A54]" />}
      </span>
      <Icon size={20} className="text-[#191A54]" />
      <span className="font-semibold text-slate-800">{label}</span>
    </button>
  );
  return (
    <div>
      <p className="text-xs font-semibold text-slate-500 mb-2">{t("pm.choose")}</p>
      <div className="space-y-2.5">
        {opt("card", CreditCard, t("pm.card"))}
        {opt("sepa_debit", Landmark, t("pm.iban"))}
      </div>
    </div>
  );
};

export const PaymentMethodSummary = ({ info, onChange }) => {
  const { t } = useLang();
  if (!info) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600" data-testid="pm-summary">
      {info.type === "sepa_debit" ? <Landmark size={16} /> : <CreditCard size={16} />}
      {info.has_method
        ? <span>{t("pm.saved")} : <span className="font-semibold capitalize">{info.brand}</span> •••• {info.last4}</span>
        : <span>{t("pm.none")}</span>}
      <button type="button" onClick={onChange} className="font-semibold text-[#191A54] underline-offset-2 hover:underline" data-testid="pm-change">
        {info.has_method ? t("pm.change") : t("pm.add")}
      </button>
    </div>
  );
};
